import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const Services = () => {
  return (
    <>
      <Helmet>
        <title>Temple Services & Programs - Shree Ram Ranuja Dham</title>
        <meta name="description" content="Participate in our daily rituals, spiritual discourses, and community service programs guided by divine wisdom at Shree Ram Ranuja Dham." />
      </Helmet>

      {/* ── Page Header ─────────────────────────────────── */}
      <section className="pt-24 pb-12 bg-gradient-to-r from-purple-600 via-orange-500 to-red-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-4">
            <span className="inline-block bg-white bg-opacity-20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              🕉️ Temple Services & Spiritual Programs
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
            Sacred Services &<br />
            <span className="text-yellow-350">Divine Programs</span>
          </h1>
          <p className="text-xl text-white opacity-95 max-w-3xl mx-auto leading-relaxed font-medium">
            Participate in our daily rituals, spiritual discourses, and community service programs guided by Bapuji's divine wisdom
          </p>
        </div>
      </section>

      {/* ── Daily Services Section ─────────────────────── */}
      <section className="py-20 bg-gradient-to-b from-orange-50 to-white temple-pattern">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Daily Temple Services</h2>
            <p className="text-xl text-gray-650 max-w-3xl mx-auto">
              Join us for our daily spiritual practices and connect with the divine
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Morning Aarti */}
            <div className="temple-card p-8 rounded-lg shadow-md text-center bg-white border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow">
                <i className="fas fa-sun text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Morning Aarti</h3>
              <p className="text-gray-700 mb-6 leading-relaxed text-sm md:text-base">
                Start your day with divine blessings through our morning aarti ceremony with devotional songs and prayers.
              </p>
              <div className="bg-orange-50 p-4 rounded-lg mb-4">
                <p className="text-orange-600 font-bold text-sm md:text-base">
                  <i className="fas fa-clock mr-2"></i>6:00 AM - 7:00 AM
                </p>
                <p className="text-xs text-gray-500 mt-1 font-semibold">Daily</p>
              </div>
              <Link to="/contact" className="btn-sacred inline-block w-full py-2.5 rounded-xl text-center bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold transition-all shadow hover:shadow-md">Join Us</Link>
            </div>

            {/* Meditation */}
            <div className="temple-card p-8 rounded-lg shadow-md text-center bg-white border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow">
                <i className="fas fa-lotus text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Meditation Sessions</h3>
              <p className="text-gray-700 mb-6 leading-relaxed text-sm md:text-base">
                Experience inner peace through guided meditation sessions led by experienced practitioners.
              </p>
              <div className="bg-purple-50 p-4 rounded-lg mb-4">
                <p className="text-purple-600 font-bold text-sm md:text-base">
                  <i className="fas fa-clock mr-2"></i>5:30 AM - 6:30 AM
                </p>
                <p className="text-xs text-gray-500 mt-1 font-semibold">Daily</p>
              </div>
              <Link to="/contact" className="btn-sacred inline-block w-full py-2.5 rounded-xl text-center bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold transition-all shadow hover:shadow-md">Join Us</Link>
            </div>

            {/* Evening Aarti */}
            <div className="temple-card p-8 rounded-lg shadow-md text-center bg-white border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow">
                <i className="fas fa-moon text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Evening Aarti</h3>
              <p className="text-gray-700 mb-6 leading-relaxed text-sm md:text-base">
                End your day with gratitude and divine connection through our evening aarti ceremony.
              </p>
              <div className="bg-yellow-55 p-4 rounded-lg mb-4">
                <p className="text-yellow-600 font-bold text-sm md:text-base">
                  <i className="fas fa-clock mr-2"></i>7:00 PM - 8:00 PM
                </p>
                <p className="text-xs text-gray-500 mt-1 font-semibold">Daily</p>
              </div>
              <Link to="/contact" className="btn-sacred inline-block w-full py-2.5 rounded-xl text-center bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold transition-all shadow hover:shadow-md">Join Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Weekly Programs Section ────────────────────── */}
      <section className="py-20 bg-white border-t border-orange-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Weekly Spiritual Programs</h2>
            <p className="text-xl text-gray-650 max-w-3xl mx-auto">
              Deepen your spiritual practice with our weekly programs and community gatherings
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Sunday Events */}
            <div className="temple-card p-8 rounded-lg shadow-lg bg-gradient-to-br from-white to-orange-50/20 border border-orange-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mr-6 shadow">
                  <i className="fas fa-users text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Sunday Events</h3>
                  <p className="text-orange-600 font-semibold text-sm">Weekly Community Gathering</p>
                </div>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed text-sm md:text-base">
                Join our weekly satsang featuring spiritual discourses, devotional singing, and community prayers.
                Bapuji shares profound insights from sacred texts and answers spiritual questions.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <p className="text-orange-600 font-bold text-sm">Time</p>
                  <p className="text-gray-750 text-sm font-semibold">9:00 AM - 11:00 AM</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <p className="text-orange-600 font-bold text-sm">Day</p>
                  <p className="text-gray-750 text-sm font-semibold">Every Sunday</p>
                </div>
              </div>
              <Link to="/events" className="btn-sacred inline-block w-full py-3 rounded-xl text-center bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold transition-all shadow hover:shadow-md">View Schedule</Link>
            </div>

            {/* Bhajan Evening */}
            <div className="temple-card p-8 rounded-lg shadow-lg bg-gradient-to-br from-white to-purple-50/20 border border-purple-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-6 shadow">
                  <i className="fas fa-music text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Bhajan Evening</h3>
                  <p className="text-purple-600 font-semibold text-sm">Devotional Music Session</p>
                </div>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed text-sm md:text-base">
                Experience the divine through devotional music and singing. Learn traditional bhajans and
                participate in soul-stirring musical prayers that elevate the spirit.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-purple-600 font-bold text-sm">Time</p>
                  <p className="text-gray-750 text-sm font-semibold">7:00 PM - 8:30 PM</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-purple-600 font-bold text-sm">Day</p>
                  <p className="text-gray-750 text-sm font-semibold">Every Wednesday</p>
                </div>
              </div>
              <Link to="/events" className="btn-sacred inline-block w-full py-3 rounded-xl text-center bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold transition-all shadow hover:shadow-md">Join Session</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
